// Extracted from ProductsPage.js. Hardware variant
// selector dialog (screws / nails / lipping-patti / fasteners /
// moulding) with rate-type picker and inner multi-select cart.
import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { Input } from '../../../components/ui/input';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { Plus, Trash2 } from 'lucide-react';
import { formatINR } from '../../../lib/store';
import { getHardwareRateOptions, getHardwareRate } from '../helpers';

const FRACTIONAL_RATE_TYPES = ['per kg', 'per running feet', 'per ltr', 'per gms', 'per ml'];

export function HardwareVariantSelectorDialog({
  group,
  onClose,
  selectedVariant,
  setSelectedVariant,
  selectedRateType,
  setSelectedRateType,
  quantityInput,
  setQuantityInput,
  setQuantity,
  cart,
  onAddToSelection,
  onRemoveFromSelection,
  onFinalize,
}) {
  return (
    <Dialog open={!!group} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="border-heda-line bg-white sm:rounded-3xl max-w-md max-h-[90vh] overflow-y-auto" data-testid="hardware-variant-dialog">
        <DialogHeader>
          <DialogTitle className="text-heda-ink">Select Category</DialogTitle>
        </DialogHeader>

        {group && (
          <div className="space-y-4">
            {/* Product Info */}
            <div className="p-3 bg-heda-gold-tint rounded-xl border border-amber-200">
              <p className="font-bold text-heda-ink">{group.brand}</p>
              <p className="text-sm text-heda-sub">{group.name}</p>
              <span className="inline-block mt-2 text-xs bg-amber-50 text-amber-700 px-2 py-1 rounded-lg font-medium border border-amber-200 capitalize">
                {group.hardware_type}
              </span>
            </div>

            {/* Size/Variant Selector - Compact buttons like plywood */}
            <div>
              <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">
                Select Size ({group.hardware_variants.length} available)
              </Label>
              <div className="mt-2 flex flex-wrap gap-2">
                {group.hardware_variants.map((hv, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setSelectedVariant(hv);
                      // Set default rate type for this variant
                      const options = getHardwareRateOptions(hv);
                      if (options.length > 0) {
                        setSelectedRateType(options[0].type);
                      }
                    }}
                    className={`flex flex-col items-center justify-center px-4 py-2 rounded-xl border transition-all ${
                      hv.nail_number ? 'min-w-[120px]' : 'min-w-[70px]'
                    } ${
                      selectedVariant?.size === hv.size && selectedVariant?.nail_number === hv.nail_number
                        ? 'border-amber-700 bg-heda-ink text-white'
                        : 'border-heda-line bg-white hover:border-amber-200'
                    }`}
                    data-testid={`hardware-size-${hv.size}`}
                  >
                    {hv.nail_number ? (
                      /* Nail format: '12 NO - 2"' (inch symbol appended) */
                      <span className="font-mono text-sm font-semibold">{hv.nail_number} NO - {String(hv.size).includes('"') ? hv.size : `${hv.size}"`}</span>
                    ) : (
                      /* Regular format: size only (append inch for inch-unit types) */
                      <span className="font-mono text-sm font-semibold">{String(hv.size).includes('"') || !['screw', 'nail', 'lipping', 'fastener', 'moulding'].includes(group?.hardware_type) ? hv.size : `${hv.size}"`}</span>
                    )}
                    {hv.box_qty && (
                      <span className={`text-[10px] ${selectedVariant?.size === hv.size && selectedVariant?.nail_number === hv.nail_number ? 'text-white/80' : 'text-heda-mute'}`}>{hv.box_qty} pcs</span>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Rate Type Selector - Only show if variant has multiple rate options */}
            {selectedVariant && getHardwareRateOptions(selectedVariant).length > 1 && (
              <div>
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">Select Rate Type</Label>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {getHardwareRateOptions(selectedVariant).map((opt, i) => (
                    <button
                      key={i}
                      onClick={() => setSelectedRateType(opt.type)}
                      className={`p-2 text-sm rounded-xl border transition-all ${
                        selectedRateType === opt.type
                          ? 'bg-heda-ink text-white border-amber-700'
                          : 'bg-white text-heda-ink border-heda-line hover:border-amber-700'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Quantity */}
            {selectedVariant && (
              <div>
                <Label className="text-[12px] font-semibold uppercase tracking-wider text-heda-mute">
                  Quantity
                  {/* Decimal hint — hardware sold by weight (per kg / per running feet)
                      often goes in fractional amounts like 2.5 kg / 3.75 kg. */}
                  {FRACTIONAL_RATE_TYPES.includes(selectedRateType) && (
                    <span className="text-[10px] text-heda-mute normal-case ml-1">(decimals OK, e.g. 2.5)</span>
                  )}
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const step = FRACTIONAL_RATE_TYPES.includes(selectedRateType) ? 0.5 : 1;
                      const newQty = Math.max(step, (parseFloat(quantityInput) || 1) - step);
                      setQuantity(newQty);
                      setQuantityInput(String(newQty));
                    }}
                    className="rounded-xl h-10 w-10 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  >-</Button>
                  <Input
                    type="text"
                    inputMode="decimal"
                    value={quantityInput}
                    onChange={(e) => {
                      // Allow digits + a single decimal point. Strip anything
                      // else so the user can't paste in junk.
                      let val = e.target.value.replace(/[^0-9.]/g, '');
                      // Collapse multiple dots to a single one.
                      const firstDot = val.indexOf('.');
                      if (firstDot !== -1) {
                        val = val.slice(0, firstDot + 1) + val.slice(firstDot + 1).replace(/\./g, '');
                      }
                      setQuantityInput(val);
                      if (val) {
                        setQuantity(parseFloat(val) || 1);
                      }
                    }}
                    onBlur={() => {
                      const f = parseFloat(quantityInput);
                      if (!quantityInput || isNaN(f) || f <= 0) {
                        setQuantityInput('1');
                        setQuantity(1);
                      }
                    }}
                    className="rounded-xl bg-heda-cream/50 text-center h-10 w-20 font-bold text-lg"
                    data-testid="hardware-quantity-input"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const step = FRACTIONAL_RATE_TYPES.includes(selectedRateType) ? 0.5 : 1;
                      const newQty = (parseFloat(quantityInput) || 1) + step;
                      setQuantity(newQty);
                      setQuantityInput(String(newQty));
                    }}
                    className="h-10 w-10 bg-white border-heda-line text-heda-ink hover:bg-white hover:border-heda-gold hover:text-heda-gold"
                  >+</Button>
                </div>
              </div>
            )}

            {/* Total Calculation */}
            {selectedVariant && selectedRateType && (
              <div className="p-3 bg-heda-gold-tint rounded-xl border border-amber-200">
                <p className="text-xs text-amber-700 uppercase tracking-wide mb-1">Total Calculation</p>
                <p className="text-sm text-heda-sub">
                  {formatINR(getHardwareRate(selectedVariant, selectedRateType))}/{selectedRateType.replace('per ', '')} × {parseFloat(quantityInput) || 1}
                </p>
                <p className="text-xl font-bold font-mono-data text-heda-ink mt-1">
                  {formatINR(getHardwareRate(selectedVariant, selectedRateType) * (parseFloat(quantityInput) || 1))}
                </p>
              </div>
            )}

            {/* Add to Selection Button — accumulates the picked variant into
                the inner cart, so user can add several sizes/rate-types in
                one trip and commit them with one final click. */}
            <Button
              onClick={onAddToSelection}
              disabled={!selectedVariant || !selectedRateType}
              className="w-full bg-heda-gold hover:bg-heda-gold/90 text-white uppercase tracking-wider font-semibold h-11 disabled:opacity-50"
              data-testid="hardware-add-to-selection-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add to Selection
            </Button>

            {/* Your Selection (running tally) */}
            {cart.length > 0 && (
              <div className="border-t border-heda-line pt-3">
                <p className="text-xs text-heda-sub uppercase tracking-wide mb-2 font-semibold">
                  Your Selection ({cart.length} item{cart.length === 1 ? '' : 's'})
                </p>
                <div className="space-y-1.5 max-h-44 overflow-y-auto">
                  {cart.map((it, i) => (
                    <div key={i} className="flex items-center justify-between gap-2 bg-heda-cream/60 rounded-lg px-2.5 py-1.5">
                      <div className="flex-1 min-w-0 text-xs">
                        <span className="font-mono font-semibold text-heda-ink">
                          {(() => {
                            const sz = String(it.size).includes('"') ? it.size : `${it.size}"`;
                            return it.nail_number ? `${it.nail_number} NO - ${sz}` : sz;
                          })()}
                        </span>
                        <span className="text-heda-sub"> × {it.quantity} </span>
                        <span className="text-heda-mute">@ {formatINR(it.rate)}/{it.rateType.replace('per ', '')}</span>
                      </div>
                      <span className="font-mono-data text-xs font-semibold text-heda-olive-dark whitespace-nowrap">
                        {formatINR(it.rate * it.quantity)}
                      </span>
                      <button
                        onClick={() => onRemoveFromSelection(i)}
                        className="text-red-700 hover:bg-red-50 rounded-lg p-1"
                        aria-label="Remove from selection"
                        data-testid={`hardware-selection-remove-${i}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-between items-center mt-2 pt-2 border-t border-heda-line">
                  <span className="text-sm font-semibold text-heda-sub">Total</span>
                  <span className="font-mono-data text-base font-bold text-heda-olive-dark">
                    {formatINR(cart.reduce((s, it) => s + it.rate * it.quantity, 0))}
                  </span>
                </div>
              </div>
            )}

            {/* Final commit button — only enabled when at least one item has been added */}
            <Button
              onClick={onFinalize}
              disabled={cart.length === 0}
              className="w-full bg-heda-ink hover:bg-black text-white uppercase tracking-wider font-semibold h-12 disabled:opacity-50"
              data-testid="confirm-hardware-add-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add {cart.length > 0 ? `${cart.length} ` : ''}Item{cart.length === 1 ? '' : 's'} to Quotation
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
